// StudyDost — API Service
// src/services/api.js
// Central place for all backend API calls

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ── Axios instance ────────────────────────────
const api = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 30000, // 30s — AI responses can take time
});

// Attach JWT token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('studydost_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle 401 — clear token and redirect to login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('studydost_token');
      localStorage.removeItem('studydost_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ── Auth ──────────────────────────────────────
export const authAPI = {
  sendOTP: (phone) =>
    api.post('/auth/send-otp', { phone }),

  verifyOTP: (phone, otp, name, studentClass, requestedRole) =>
    api.post('/auth/verify-otp', { phone, otp, name, studentClass, requestedRole }),

  getMe: () =>
    api.get('/auth/me'),

  logout: () =>
    api.post('/auth/logout'),
};

// ── Questions ─────────────────────────────────
export const questionAPI = {
  ask: (questionText, studentClass, conversationHistory) =>
    api.post('/questions/ask', { questionText, studentClass, conversationHistory }),

  askWithImage: (imageBase64, mimeType, caption, studentClass) =>
    api.post('/questions/ask-image', { imageBase64, mimeType, caption, studentClass }),

  getHistory: (page = 1) =>
    api.get(`/questions/history?page=${page}`),

  getUsage: () =>
    api.get('/questions/usage'),

  rate: (questionId, rating) =>
    api.post(`/questions/${questionId}/rate`, { rating }),
};

// ── Payments ──────────────────────────────────
export const paymentAPI = {
  createOrder: (planType, questionId = null) =>
    api.post('/payments/create-order', { planType, questionId }),

  getHistory: () =>
    api.get('/payments/history'),
};

// ── Users ─────────────────────────────────────
export const userAPI = {
  getProfile: () =>
    api.get('/users/profile'),

  updateProfile: (data) =>
    api.patch('/users/profile', data),
};

// ── Teacher ───────────────────────────────────
export const teacherAPI = {
  getReferralInfo: () => api.get('/teacher/referral-info').then(r => r.data),
  getEarnings: () => api.get('/teacher/earnings').then(r => r.data),
  getStudents: () => api.get('/teacher/students').then(r => r.data),
  getAnalytics: () => api.get('/teacher/analytics').then(r => r.data),
};

export default api;
